import {
  computed,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import { AppConfigService } from '@config/app-config.service';
import { MaxFolloweeError } from '@error/max-followee.error';
import { EventService } from '@event/event.service';
import { AppConfig } from '@model/app-config';
import { Event } from '@model/event';
import { Followee } from '@model/followee';
import { Lap } from '@model/lap';
import { Participant } from '@model/participant';
import { PushNotificationService } from '@notification/push-notification.service';
import { ParticipantService } from '@participant/participant.service';
import { Constants } from '@shared/constants';
import { appDatabase } from '@shared/database/app-database';
import { Team } from '@team/team';
import {
  createFollowee,
  enrichFollowees,
  mergeFollowees,
  sortFollowees,
} from '@util/followee-util';
import {
  forkJoin,
  from,
  lastValueFrom,
  Observable,
  of,
  Subscription,
  timer,
} from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class FolloweeService {
  private readonly eventService = inject(EventService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly pushNotificationService = inject(PushNotificationService);
  private readonly participantService = inject(ParticipantService);

  private table = appDatabase.followee;

  private event = this.eventService.event;
  private timerSubscription?: Subscription;

  private _followees = signal<Followee[] | undefined>(undefined);
  followees = computed<Followee[]>(() =>
    sortFollowees(this._followees() ?? [], this.event()),
  );

  maxFollowees = computed<number>(
    () => this.appConfigService.appConfig()?.maxFollowees ?? Constants.MAX_FOLLOWEES,
  );

  maxFolloweesReached = computed<boolean>(
    () => this.followees().length >= this.maxFollowees(),
  );

  unreadFollowees = computed<number>(() =>
    this.event()?.isEventLive
      ? (this._followees() ?? []).filter(followee => followee.isRead === false)
        .length
      : 0,
  );

  constructor() {
    effect(() => {
      const event = this.event();
      const appConfig = this.appConfigService.appConfig();

      this.timerSubscription?.unsubscribe();

      if (event?.isEventLive) {
        this.startFolloweesInterval(appConfig);
      }
    });

    effect(() => {
      const followees = this._followees();
      const event = this.eventService.event();

      if (event) {
        void this.processTableItems(event, followees);
      }
    });

    effect(() => {
      // We listen to a new lapNotification but won't process the result
      // The new list of notifications will be taken from the backend
      const _lapNotification = this.pushNotificationService.lapNotification();

      if (_lapNotification) {
        untracked(() => this.processFolloweeLaps().subscribe());
      }
    });

    effect(() => {
      // We listen to a new liveNotification but won't process the result
      // The new list of notifications will be taken from the backend and the interval timer will be reset
      const _liveNotification = this.pushNotificationService.liveNotification();

      if (_liveNotification) {
        untracked(() =>
          this.processFolloweeLaps().subscribe(() =>
            this.startFolloweesInterval(),
          ),
        );
      }
    });
  }

  init(): Observable<Followee[]> {
    return this.cleanupFollowees().pipe(
      mergeMap(() => this.processFollowees()),
    );
  }

  private cleanupFollowees(): Observable<void> {
    const event = this.event();

    if (!event) {
      return of(undefined);
    }

    return from(
      this.table
        .where(Constants.DATABASE_KEY_IDENTIFIER)
        .notEqual(event.code)
        .delete(),
    ).pipe(map(() => undefined));
  }

  private processFollowees(): Observable<Followee[]> {
    const event = this.event();

    const currentFollowees = this._followees();

    // Do not update the followee details when the event is live
    if (currentFollowees !== undefined && event?.isEventLive) {
      return of(currentFollowees);
    }

    // Get the stored followees
    return from(this.table.toArray()).pipe(
      mergeMap(storedFollowees => {
        const uuids = storedFollowees.map(followee => followee.uuid);

        return forkJoin([
          of(storedFollowees as Followee[]),
          this.participantService.getParticipants(uuids),
        ]);
      }),
      map(([storedFollowees, remoteParticipants]) => {
        const mergedFollowees = mergeFollowees(
          storedFollowees,
          remoteParticipants,
        );
        this._followees.set(mergedFollowees);

        return mergedFollowees;
      }),
    );
  }

  private startFolloweesInterval(appConfig?: AppConfig): void {
    this.timerSubscription?.unsubscribe();

    const followeesUpdateInterval =
      (appConfig?.followeesUpdateInterval ??
        Constants.FOLLOWEES_UPDATE_INTERVAL_MINUTES) * Constants.A_MINUTE;

    const followeesIntervalInitialWait = Constants.FOLLOWEES_INTERVAL_INITIAL_WAIT_SECONDS * Constants.A_SECOND;

    this.timerSubscription = timer(
      followeesIntervalInitialWait,
      followeesUpdateInterval,
    )
      .pipe(mergeMap(() => this.processFolloweeLaps()))
      .subscribe();
  }

  // Process the followees (laps) on resume of the app when there are participants or the event is live
  onResume(): Observable<void> {
    const event = this.eventService.event();

    // Return if no event or no participant content available
    if (!event || (!event.isEventLive && !event.showParticipants)) {
      return of(undefined);
    }

    // Process the followees, and the laps when the event is live
    return this.processFollowees().pipe(
      mergeMap(() =>
        event.isEventLive ? this.processFolloweeLaps() : of(undefined),
      ),
    );
  }

  private async processTableItems(
    event: Event,
    followees?: Followee[],
  ): Promise<void> {
    // No processing when notifications is undefined
    // Otherwise the stored data is deleted on app init
    if (followees !== undefined) {
      // Check the stored notifications in the table against the new list, bulkDelete by the found deleted uuids
      const storedFollowees = await this.table.toArray();
      const deletedFollowees = storedFollowees.filter(
        storedFollowee =>
          followees.find(followee => followee.uuid === storedFollowee.uuid) ===
          undefined,
      );
      const deletedFolloweeUuids = deletedFollowees.map(
        deletedFollowee => deletedFollowee.uuid,
      );
      await this.table.bulkDelete(deletedFolloweeUuids);

      await this.table.bulkPut(
        followees.map(followee => ({
          ...followee,
          key: event.code,
        })),
      );
    }
  }

  private processFolloweeLaps(): Observable<void> {
    const followees = this._followees() ?? [];

    return this.participantService.getLiveLaps(followees).pipe(
      tap(liveLaps => {
        const enrichedFollowees = enrichFollowees(
          followees,
          this.event()?.isEventLive ?? false,
          liveLaps,
        );

        this._followees.update(currentFollowees => {
          return currentFollowees?.map(currentFollowee => {
            const enrichedFollowee = enrichedFollowees.find(
              item => item.uuid === currentFollowee.uuid,
            );

            return enrichedFollowee ?? currentFollowee;
          });
        });
      }),
      map(() => undefined),
    );
  }

  markAsRead(followee: Followee): void {
    this._followees.update(followees => {
      if (followees) {
        return followees.map(item => ({
          ...item,
          isRead: item.uuid === followee.uuid ? true : item.isRead,
        }));
      }
    });
  }

  async followParticipant(participant: Participant): Promise<void> {
    return this.followParticipants([participant]);
  }

  async followParticipants(participants: Participant[]): Promise<void> {
    const isEventLive = this.event()?.isEventLive ?? false;

    const followees = this._followees() ?? [];

    /** Get a list of participants to follow, which are not followed yet */
    const participantsNotFollowed = participants.filter(
      participant =>
        followees.find(followee => followee.uuid === participant.uuid) ===
        undefined,
    );

    if (participantsNotFollowed.length === 0) {
      return;
    }

    if ((followees.length + participantsNotFollowed.length) > this.maxFollowees()) {
      throw new MaxFolloweeError();
    }

    /** Create a map with the uuid of a participant and the laps of the participant */
    const participantsLaps = isEventLive
      ? new Map<string, Lap[]>()
      : await lastValueFrom(
        this.participantService.getParticipantsLaps(participantsNotFollowed),
      );

    /** Create a list of new followees, based on the new participants and the laps per participant */
    const createdFollowees = participantsNotFollowed.map(participant =>
      createFollowee(participant, participantsLaps.get(participant.uuid)),
    );

    /** Update the internal followees list */
    this._followees.update(currentFollowees => [
      ...(currentFollowees ?? []),
      ...createdFollowees,
    ]);

    /** Subscribe to notifications of the new followees */
    await Promise.all(
      participantsNotFollowed.map(participant =>
        this.pushNotificationService.subscribeToParticipant(participant),
      ),
    );

    // When the event is live, we want the most actual lap information for all followees
    if (isEventLive) {
      await lastValueFrom(this.processFolloweeLaps());
    }
  }

  async followTeam(team: Team): Promise<void> {
    const participants = await lastValueFrom(
      this.participantService.getParticipantsOfTeam(team),
    );
    await this.followParticipants(participants);
  }

  async unfollow(participant: Participant): Promise<void> {
    this._followees.update(followees =>
      (followees ?? []).filter(followee => followee.uuid !== participant.uuid),
    );

    await this.pushNotificationService.unsubscribeFromParticipant(participant);
  }
}
